import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { getIic } from "../../services/data.service";
import assetUrl from "../../services/assetUrl";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PageBanner from "../../components/PageBanner";
import highlight from "../home/highlight";
import "../home/homeRedesign.css"; // shared primitives: .eyebrow-ed, .heading-ed, .hl-ed
import "./iicNew.css";

const EASE = [0.23, 1, 0.32, 1];
const containerV = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const itemV = {
  hidden: { opacity: 0, transform: "translateY(20px)" },
  show: { opacity: 1, transform: "translateY(0px)", transition: { duration: 0.45, ease: EASE } },
};
const Rise = ({ children, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, transform: "translateY(26px)" }} whileInView={{ opacity: 1, transform: "translateY(0px)" }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, ease: EASE }}>
    {children}
  </motion.div>
);

/* ---------------- icons ---------------- */
const sp = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
const STAT_ICONS = {
  innovators: (<svg {...sp}><circle cx="9" cy="8" r="3.1" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0" /><path d="M16 6.2a3 3 0 0 1 0 5.6" /><path d="M17 14.4A5.5 5.5 0 0 1 20.5 19" /></svg>),
  ideas: (<svg {...sp}><path d="M5 15c-1.5 1-2 5-2 5s4-.5 5-2a2.1 2.1 0 0 0-3-3Z" /><path d="M9 14c5-2 9-7 9-11 0 0-6 0-11 5-2 2-2 4-2 4l4 4s2 0 4-2Z" /><circle cx="14.5" cy="8.5" r="1.3" /></svg>),
  events: (<svg {...sp}><path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4Z" /><path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" /></svg>),
};
const FUNC_ICONS = {
  culture: (<svg {...sp}><circle cx="12" cy="8" r="3.4" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /></svg>),
  programs: (<svg {...sp}><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4M8 9h8M8 12.5h5" /></svg>),
  prototype: (<svg {...sp}><path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" /><path d="m12 12 8-4.5M12 12v9M12 12 4 7.5" /></svg>),
  ecosystem: (<svg {...sp}><circle cx="6" cy="7" r="2.4" /><circle cx="18" cy="7" r="2.4" /><circle cx="12" cy="18" r="2.4" /><path d="M7.6 8.8 10.6 16M16.4 8.8 13.4 16M8 7h8" /></svg>),
  startups: (<svg {...sp}><path d="M5 15c-1.5 1-2 5-2 5s4-.5 5-2a2.1 2.1 0 0 0-3-3Z" /><path d="M9 14c5-2 9-7 9-11 0 0-6 0-11 5-2 2-2 4-2 4l4 4s2 0 4-2Z" /></svg>),
  recognition: (<svg {...sp}><circle cx="12" cy="9" r="5" /><path d="m8.5 13-1.5 8 5-3 5 3-1.5-8" /></svg>),
};
const IconSpark = (<svg {...sp}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" /></svg>);
const FOCUS_ICONS = [
  (<svg {...sp}><circle cx="6" cy="7" r="2.2" /><circle cx="18" cy="7" r="2.2" /><circle cx="12" cy="18" r="2.2" /><path d="M7.6 8.6 10.8 15.8M16.4 8.6 13.2 15.8M8 7h8" /></svg>),
  (<svg {...sp}><path d="M5 15c-1.5 1-2 5-2 5s4-.5 5-2a2.1 2.1 0 0 0-3-3Z" /><path d="M9 14c5-2 9-7 9-11 0 0-6 0-11 5-2 2-2 4-2 4l4 4s2 0 4-2Z" /><circle cx="14.5" cy="8.5" r="1.2" /></svg>),
  (<svg {...sp}><circle cx="12" cy="9" r="5" /><path d="m8.5 13-1.5 8 5-3 5 3-1.5-8" /></svg>),
  (<svg {...sp}><path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 0 0-4 10.5c.8.8 1 1.3 1 2.5h6c0-1.2.2-1.7 1-2.5A6 6 0 0 0 12 3Z" /></svg>),
  (<svg {...sp}><path d="M9.5 4A2.5 2.5 0 0 0 7 6.5 2.5 2.5 0 0 0 5 11a2.5 2.5 0 0 0 2 4 2 2 0 0 0 4 0V4.5A2 2 0 0 0 9.5 4Z" /><path d="M14.5 4A2.5 2.5 0 0 1 17 6.5 2.5 2.5 0 0 1 19 11a2.5 2.5 0 0 1-2 4 2 2 0 0 1-4 0" /></svg>),
];
const OBJ_ICONS = [
  (<svg {...sp}><path d="M21 11.5a8.4 8.4 0 0 1-11.6 7.7L3 21l1.8-6.4A8.4 8.4 0 1 1 21 11.5Z" /></svg>),
  (<svg {...sp}><path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 0 0-4 10.5c.8.8 1 1.3 1 2.5h6c0-1.2.2-1.7 1-2.5A6 6 0 0 0 12 3Z" /></svg>),
  (<svg {...sp}><path d="m11 17 2 2a1.5 1.5 0 0 0 2.1-2.1M13 15l2 2a1.5 1.5 0 0 0 2.1-2.1L13 10.5" /><path d="m18 14 2.5-2.5a2 2 0 0 0 0-2.8L17 5l-3 1" /><path d="M3.5 8.7 7 5l4 2-3 3a1.6 1.6 0 0 1-2.3 0 1.6 1.6 0 0 1 0-2.3" /></svg>),
  (<svg {...sp}><path d="M22 10 12 5 2 10l10 5 10-5z" /><path d="M6 12v5c0 1 2.7 2 6 2s6-1 6-2v-5" /></svg>),
  (<svg {...sp}><circle cx="9" cy="9" r="5" /><path d="M14.5 5.3a5 5 0 0 1 0 13.4" /><path d="M7.5 9h3M9 7.5v3" /></svg>),
  (<svg {...sp}><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5.3 5.3 7.4 7.4M16.6 16.6l2.1 2.1M18.7 5.3 16.6 7.4M7.4 16.6l-2.1 2.1" /></svg>),
  (<svg {...sp}><path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" /><path d="m12 12 8-4.5M12 12v9M12 12 4 7.5" /></svg>),
  (<svg {...sp}><path d="M12 3 4 6v6c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V6l-8-3Z" /><path d="m9 12 2 2 4-4" /></svg>),
];
const IconEye = (<svg {...sp}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></svg>);
const IconRocket = (<svg {...sp}><path d="M5 15c-1.5 1-2 5-2 5s4-.5 5-2a2.1 2.1 0 0 0-3-3Z" /><path d="M9 14c5-2 9-7 9-11 0 0-6 0-11 5-2 2-2 4-2 4l4 4s2 0 4-2Z" /><circle cx="14.5" cy="8.5" r="1.3" /></svg>);
const IconArrow = (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>);
const IconExternal = (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h13M12 6l6 6-6 6" /></svg>);
const Chevron = ({ dir }) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={dir === "prev" ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"} /></svg>);

/* ---------------- helpers ---------------- */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const parseDate = (d = "") => {
  const first = String(d).split(/[–-]/)[0].trim();
  const [dd, mm] = first.split(".");
  return { day: (dd || "").padStart(2, "0"), month: MONTHS[(parseInt(mm, 10) || 1) - 1] || "" };
};
const categoryOf = (title = "") => {
  const t = title.toLowerCase();
  if (/workshop|boot-?camp/.test(t)) return { label: "Workshop", tone: "orange" };
  if (/session/.test(t)) return { label: "Session", tone: "blue" };
  if (/talk|lecture|expert|guest|motivat|story|address/.test(t)) return { label: "Talk", tone: "orange" };
  if (/visit|incubat|tour/.test(t)) return { label: "Visit", tone: "green" };
  if (/competition|hackathon|contest|expo|conference|presentation|day|celebrat/.test(t)) return { label: "Competition", tone: "navy" };
  if (/meeting|council/.test(t)) return { label: "Meeting", tone: "blue" };
  return { label: "Event", tone: "navy" };
};
const initials = (name = "") => {
  const w = name.replace(/^(Dr|Prof|Mr|Mrs|Ms|Sri|Smt)\.?\s+/i, "").trim().split(/\s+/);
  return ((w[0]?.[0] || "") + (w[1]?.[0] || "")).toUpperCase() || "N";
};
const roleTone = (pos = "") => {
  const p = pos.toLowerCase();
  if (/president|head of/.test(p)) return "orange";
  if (/conven/.test(p)) return "blue";
  if (/principal|head/.test(p)) return "navy";
  if (/\bmember\b/.test(p)) return "slate";
  return "violet";
};

const SectionHead = ({ eyebrow, heading, hl, onDark = false, center = true }) => (
  <div className={`iix-head${center ? " iix-head--center" : ""}`}>
    <span className={`eyebrow-ed${onDark ? " eyebrow-ed--on-dark" : ""}`}>{eyebrow}</span>
    <h2 className={`heading-ed${onDark ? " heading-ed--on-dark" : ""}`}>{hl ? highlight(heading, hl) : heading}</h2>
  </div>
);

const IIC = () => {
  const [iicData, setIicData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [activeAct, setActiveAct] = useState(null);
  const [activeMem, setActiveMem] = useState(null);
  const [showAllEvents, setShowAllEvents] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchData = async () => {
      try {
        const res = await getIic();
        if (res && res.success && res.data && res.data.data) {
          const fetchedData = res.data.data;
          setIicData(fetchedData);
          if (fetchedData.activities) {
            setActiveAct(Object.keys(fetchedData.activities)[0]);
          }
          if (fetchedData.members) {
            setActiveMem(Object.keys(fetchedData.members)[0]);
          }
        }
      } catch (err) {
        console.error("Error fetching IIC data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => { setShowAllEvents(false); }, [activeAct]);

  if (loading) {
    return (
      <>
        <Header parentMenu="outreach" topbarEnable="enable" />
        <div style={{ padding: "100px 20px", textAlign: "center" }}>Loading IIC Content...</div>
        <Footer />
      </>
    );
  }

  if (!iicData) {
    return (
      <>
        <Header parentMenu="outreach" topbarEnable="enable" />
        <div style={{ padding: "100px 20px", textAlign: "center" }}>No IIC Content Found.</div>
        <Footer />
      </>
    );
  }

  const { banner, about, focusTitle, focus, functions, activities, members, edCell, reports, gallery, contact } = iicData;
  const activityTabs = Object.keys(activities || {});
  const memberVersions = Object.keys(members || {});

  const allEvents = activities[activeAct] || [];
  const EVENTS_INITIAL = 5;
  const events = showAllEvents ? allEvents : allEvents.slice(0, EVENTS_INITIAL);
  const memberRows = members[activeMem] || [];

  return (
    <div className="iix-page">
      <Header parentMenu="outreach" topbarEnable="enable" />

      {/* ---- Hero (existing PageBanner) ---- */}
      <PageBanner
        className="iix-banner"
        title="Institution's Innovation Council"
        eyebrow="Innovation · Entrepreneurship"
        subtitle={banner.description}
        image={banner.bannerImage ? assetUrl(banner.bannerImage) : undefined}
      />

      {/* ---- About + Focus ---- */}
      <section className="iix-sec iix-about-sec">
        <div className="container">
          <div className="iix-about__grid">
            <Rise className="iix-about__left">
              <span className="eyebrow-ed">{about.eyebrow}</span>
              <h2 className="heading-ed iix-about__heading">{highlight(about.heading, about.highlights)}</h2>
              <p className="iix-about__text">{about.summary}</p>
              <div className="iix-stats">
                {about.stats.map((s, i) => (
                  <div className="iix-stat" key={i}>
                    <span className="iix-stat__ic">{STAT_ICONS[s.icon]}</span>
                    <span className="iix-stat__num">{s.value}</span>
                    <span className="iix-stat__label">{s.label}</span>
                  </div>
                ))}
              </div>
            </Rise>

            <Rise className="iix-focus">
              <span className="iix-focus__eyebrow">{focusTitle}</span>
              <ul className="iix-focus__list">
                {focus.map((f, i) => (
                  <li className="iix-focus__item" key={i}>
                    <span className="iix-focus__ic">{FOCUS_ICONS[i % FOCUS_ICONS.length]}</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <span className="iix-focus__dots" aria-hidden="true" />
            </Rise>
          </div>
        </div>
      </section>

      {/* ---- Functions ---- */}
      <section className="iix-sec iix-funcs-sec">
        <div className="container">
          <Rise><SectionHead eyebrow="Key Mandates" heading="Functions of IICs" hl="IICs" /></Rise>
          <motion.div className="iix-funcs" variants={containerV} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
            {functions.map((fn, i) => (
              <motion.article className="iix-func" key={i} variants={itemV}>
                <span className={`iix-func__icon iix-func__icon--${fn.tone}`}>{FUNC_ICONS[fn.icon]}</span>
                <h3 className="iix-func__title">{fn.title}</h3>
                <p className="iix-func__text">{fn.text}</p>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ---- Activities ---- */}
      <section className="iix-sec iix-acts-sec">
        <div className="container">
          <Rise><SectionHead eyebrow="Events & Engagements" heading="IIC Activities" onDark /></Rise>
          <Rise>
            <div className="iix-tabs" role="tablist">
              {activityTabs.map((t) => (
                <button key={t} type="button" role="tab" aria-selected={activeAct === t} className={`iix-tab${activeAct === t ? " is-active" : ""}`} onClick={() => setActiveAct(t)}>
                  IIC {t}
                </button>
              ))}
            </div>
          </Rise>
          <Rise>
            <div className={`iix-events${showAllEvents ? " is-scroll" : ""}`}>
              {events.map((ev, i) => {
                const { day, month } = parseDate(ev.date);
                const cat = categoryOf(ev.title);
                const tone = i % 2 ? "orange" : "navy"; // theme-only, alternating
                return (
                  <article className="iix-event" key={i}>
                    <div className="iix-event__top">
                      <span className={`iix-event__date iix-event__date--${tone}`}>
                        <strong>{day}</strong><small>{month}</small>
                      </span>
                      <span className={`iix-event__tag iix-event__tag--${tone}`}>{cat.label}</span>
                    </div>
                    <h3 className="iix-event__title">{ev.title}</h3>
                    {ev.link && (
                      <a className="iix-event__link" href={ev.link} target="_blank" rel="noopener noreferrer">
                        View Details {IconExternal}
                      </a>
                    )}
                  </article>
                );
              })}
            </div>
          </Rise>
          {!showAllEvents && events.length > 1 && (
            <div className="iix-acts__rail" aria-hidden="true">
              <span className="iix-acts__line" />
              {events.map((_, i) => <span className="iix-acts__dot" key={i} />)}
            </div>
          )}
          {allEvents.length > EVENTS_INITIAL && (
            <div className="iix-acts__more">
              <button type="button" className="iix-acts__morebtn" onClick={() => setShowAllEvents((v) => !v)}>
                {showAllEvents ? "Show Less" : "Explore More Activities"} {IconArrow}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ---- Members ---- */}
      <section className="iix-sec iix-members-sec">
        <div className="container">
          <Rise><SectionHead eyebrow="Our Innovation Leaders" heading="IIC Members" /></Rise>
          <Rise>
            <div className="iix-tabs iix-tabs--light" role="tablist">
              {memberVersions.map((t) => (
                <button key={t} type="button" role="tab" aria-selected={activeMem === t} className={`iix-tab iix-tab--light${activeMem === t ? " is-active" : ""}`} onClick={() => setActiveMem(t)}>
                  IIC {t}
                </button>
              ))}
            </div>
          </Rise>
          <Rise className="iix-members__wrap">
            <button type="button" className="iix-mem-nav iix-mem-prev" aria-label="Previous"><Chevron dir="prev" /></button>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              observer={true}
              observeParents={true}
              navigation={{ prevEl: ".iix-mem-prev", nextEl: ".iix-mem-next" }}
              pagination={{ el: ".iix-mem-dots", clickable: true }}
              autoplay={{ delay: 3500, disableOnInteraction: false, pauseOnMouseEnter: true }}
              loop={memberRows.length > 5}
              spaceBetween={20}
              slidesPerView={1}
              breakpoints={{ 768: { slidesPerView: 2 }, 1024: { slidesPerView: 3 }, 1280: { slidesPerView: 5 } }}
            >
              {memberRows.map((m, i) => (
                <SwiperSlide key={i} style={{ height: "auto" }}>
                  <article className="iix-mcard">
                    <span className={`iix-mcard__avatar iix-mcard__avatar--${i % 2 ? "orange" : "navy"}`}>{initials(m.name)}</span>
                    <h3 className="iix-mcard__name">{m.name}</h3>
                    <p className="iix-mcard__desig">{m.designation}</p>
                    <span className={`iix-mcard__role iix-mcard__role--${i % 2 ? "orange" : "navy"}`}>{m.position}</span>
                  </article>
                </SwiperSlide>
              ))}
            </Swiper>
            <button type="button" className="iix-mem-nav iix-mem-next" aria-label="Next"><Chevron dir="next" /></button>
          </Rise>
          <div className="iix-mem-dots" />
        </div>
      </section>

      {/* ---- ED Cell ---- */}
      <section className="iix-sec iix-ed-sec">
        <div className="container">
          <div className="iix-ed__top">
            <Rise className="iix-ed__intro">
              <span className="eyebrow-ed">Entrepreneurship</span>
              <h2 className="heading-ed">Entrepreneurship Development Cell</h2>
              <p className="iix-ed__text">{edCell.intro}</p>
            </Rise>
            <Rise className="iix-ed__vm">
              <div className="iix-vm iix-vm--vision">
                <span className="iix-vm__ic">{IconEye}</span>
                <div className="iix-vm__body">
                  <h3 className="iix-vm__title">Vision</h3>
                  <p className="iix-vm__text">{edCell.vision}</p>
                </div>
              </div>
              <div className="iix-vm iix-vm--mission">
                <span className="iix-vm__ic">{IconRocket}</span>
                <div className="iix-vm__body">
                  <h3 className="iix-vm__title">Mission</h3>
                  <p className="iix-vm__text">{edCell.mission}</p>
                </div>
              </div>
            </Rise>
          </div>

          <Rise>
            <div className="iix-obj">
              <span className="iix-obj__eyebrow">Our Objectives</span>
              <div className="iix-obj__grid">
                <ul className="iix-obj__list">
                  {edCell.objectives.map((o, i) => (
                    <li className="iix-obj__item" key={i}>
                      <span className={`iix-obj__ic iix-obj__ic--${i % 2 ? "orange" : "navy"}`}>{OBJ_ICONS[i % OBJ_ICONS.length]}</span>
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
                <div className="iix-obj__art" aria-hidden="true">
                  <img src={assetUrl(edCell.objectiveImage || "/images/iic-page/objective.png")} alt="" loading="lazy" />
                </div>
              </div>
            </div>
          </Rise>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IIC;
